import { AbstractCollection } from '../../abstract/Collection';
import { AbstractDocument, Identifiable, Timestampable } from '../../abstract/Document';
import { Supplier, Suppliers } from '../Suppliers';
import { type IConfiguration, type IConfigurationLeadsStage, ILeadDefaultStageId } from './Configurations.types';
import { type ILead, ILeadReadState, ILeadRepliedState, ILeadSource } from './Leads.types';
import { Notes } from './Leads/Notes';
import { ScheduledMessages } from './Leads/ScheduledMessages';
import {
  arrayRemove,
  arrayUnion,
  deleteField,
  type PartialWithFieldValue,
  runTransaction,
  serverTimestamp,
  where,
  type WithFieldValue,
} from 'firebase/firestore';
import { splitEvery } from 'ramda';

/**
 * List of external lead sources.
 * Used to identify leads that were not created from Bridebook's couple side.
 */
export const LEAD_SOURCE_EXTERNAL: ILeadSource[] = [ILeadSource.ChatGPT, ILeadSource.Email, ILeadSource.Import, ILeadSource.Widget];

@Identifiable
@Timestampable
export class Lead extends AbstractDocument<ILead> {
  readonly collections = {
    Notes: new Notes(this),
    ScheduledMessages: new ScheduledMessages(this),
  };

  get supplier() {
    return Suppliers._.getById(this.reference.parent.parent!.id);
  }

  /**
   * Calculates the score of the lead from the weights of the different stage transitions.
   *
   * @param snapshot The `Lead` snapshot, optional.
   */
  async getScore(snapshot?: ILead) {
    /**
     * If the snapshot is not provided, fetch the latest snapshot.
     */
    if (snapshot == null) {
      snapshot = await this.get(true);
    }

    let result = 1;

    /**
     * Sort the stages by their timestamp and map them to their keys (IDs).
     */
    const stages = Object.entries(snapshot.stages)
      .sort((a, b) => a[1].toMillis() - b[1].toMillis())
      .map(([key]) => key);

    if (stages.length > 0) {
      const configurations = await this.supplier.Configurations.query<IConfiguration & IConfigurationLeadsStage>([
        where('id', 'in', stages),
      ]).get(true);

      for (const stage of stages) {
        if (configurations[stage]?.weight != null) {
          result *= configurations[stage].weight!;
        }
      }
    }

    return result;
  }

  /**
   * Derives the read and replied state of the lead from the `read` and `replied` temporal booleans.
   *
   * @param snapshot The `Lead` snapshot, optional.
   */
  async getState(snapshot?: ILead): Promise<{ read: ILeadReadState; replied: ILeadRepliedState }> {
    /**
     * If the snapshot is not provided, fetch the latest snapshot.
     */
    if (snapshot == null) {
      snapshot = await this.get(true);
    }

    const result = {
      read: snapshot.actions.read === false ? ILeadReadState.Unread : ILeadReadState.Read,
      replied: snapshot.actions.replied === false ? ILeadRepliedState.NotReplied : ILeadRepliedState.Replied,
    };

    /**
     * If the lead has never been read, we return the "New" state.
     */
    if (snapshot.actions.read == null) {
      result.read = ILeadReadState.New;
    }

    return result;
  }

  /**
   * Returns a boolean indicating whether the lead is a PowerMatch invitation.
   */
  async isPowerMatch(snapshot?: ILead) {
    /**
     * If the snapshot is not provided, fetch the latest snapshot.
     */
    if (snapshot == null) {
      snapshot = await this.get(true);
    }

    if (snapshot.origin.source === ILeadSource.PowerMatch && snapshot.stage === ILeadDefaultStageId.Invitation) {
      return true;
    }

    return false;
  }

  /**
   * Marks the lead as read, using the current timestamp.
   *
   * IMPORTANT: This function needs to be called in a transaction to ensure that we only receive an
   * update from the server, not from the client, as the client would return actions.read timestamp
   * as `null` which is one of its valid states, causing a flicker in the UI.
   */
  markAsRead() {
    return runTransaction(this.reference.firestore, async (transaction) => {
      transaction.set(
        this.reference,
        {
          actions: {
            read: serverTimestamp(),
          },
          updatedAt: serverTimestamp(),
        },
        {
          merge: true,
        },
      );
    });
  }

  /**
   * Marks the lead as unread, preserving the `null` value if the lead has never been read.
   */
  markAsUnread() {
    return runTransaction(this.reference.firestore, async (transaction) => {
      const data = (await transaction.get(this.reference)).data();

      if (data?.actions.read != null) {
        transaction.set(
          this.reference,
          {
            actions: {
              read: false,
            },
            updatedAt: serverTimestamp(),
          },
          {
            merge: true,
          },
        );
      }
    });
  }

  /**
   * Sets a single action on the document.
   *
   * @param action The action to set.
   * @param value The value to set, can be either a timestamp or `false` (or `null` in case of
   *   "read").
   */
  setAction<Actions = ILead['actions'], Action extends keyof Actions = keyof Actions>(
    action: Action,
    value: WithFieldValue<Actions>[Action],
  ) {
    return this.setActions({
      [action]: value,
    });
  }

  /**
   * Sets multiple actions on the document.
   *
   * @param actions A key-value pair of actions to set. The values can be either timestamps or
   *   `false` (or `null` in case of "read").
   */
  setActions(actions: PartialWithFieldValue<ILead['actions']>) {
    return this.set(
      {
        actions,
      },
      true,
    );
  }

  /**
   * Set label on the document.
   *
   * @param labelId The ID of the label to be set.
   */
  setLabel(labelId: NonNullable<ILead['labels']>[number]) {
    return this.set({
      labels: arrayUnion(labelId),
    });
  }

  removeLabel(labelId: NonNullable<ILead['labels']>[number]) {
    return this.set({
      labels: arrayRemove(labelId),
    });
  }

  removeAllLabels() {
    return this.set({
      labels: deleteField(),
    });
  }

  /**
   * Set stage on the document.
   *
   * @param stageId The ID of the stage to be set.
   */
  setStage(stageId: ILead['stage']) {
    return this.set({
      stage: stageId,
      stages: {
        [stageId]: serverTimestamp(),
      },
      status: deleteField(), // Business requirement: `status` should be deleted when new stage is set
    });
  }

  /**
   * Set status on the document.
   *
   * @param statusId The ID of the status to be set.
   */
  setStatus(statusId: ILead['status'] | null) {
    return this.set({
      status: statusId || deleteField(),
    });
  }

  /**
   * Set assignee on the document.
   *
   * @param assigneeUserId
   */
  setAssignee(assigneeUserId: ILead['assignee'] | null) {
    return this.set({
      assignee: assigneeUserId || deleteField(),
    });
  }
}

export class Leads extends AbstractCollection<Lead, ILead> {
  static path = 'leads';

  constructor(document: Supplier) {
    super(document.collection(Leads.path), Lead);
  }

  get supplier() {
    return Suppliers._.getById(this.reference.parent!.id);
  }

  async getAll() {
    return await this.query().get(true);
  }

  /**
   * Set `assignee` by IDs
   * @param ids The IDs of the leads for which the `assignee` will be set.
   * @param assigneeUserId The user ID of the new `assignee`.
   */
  async setAssigneeByIds(ids: ILead['id'][], assigneeUserId: ILead['assignee'] | null) {
    if (ids.length === 0) return;

    const batches = splitEvery(250, ids);

    for (const batch of batches) {
      this.begin();

      for (const id of batch) {
        await this.getById(id).setAssignee(assigneeUserId);
      }

      await this.commit();
    }

    return;
  }

  /**
   * Set `label` by IDs
   * If `labelId` is `null`, all labels will be removed.
   *
   * @param ids The IDs of the leads for which the `label` will be set.
   * @param labelId The new label ID.
   */
  async setLabelByIds(ids: ILead['id'][], labelId: NonNullable<ILead['labels']>[number] | null) {
    if (ids.length === 0) return;

    const batches = splitEvery(250, ids);

    for (const batch of batches) {
      this.begin();

      for (const id of batch) {
        if (labelId) {
          await this.getById(id).setLabel(labelId);
        } else {
          await this.getById(id).removeAllLabels();
        }
      }

      await this.commit();
    }

    return;
  }

  /**
   * Remove a specific `label` by IDs
   *
   * @param ids The IDs of the leads from which the `label` will be removed.
   * @param labelId The label ID to remove.
   */
  async removeLabelByIds(ids: ILead['id'][], labelId: NonNullable<ILead['labels']>[number]) {
    if (ids.length === 0 || !labelId) return;

    const batches = splitEvery(250, ids);

    for (const batch of batches) {
      this.begin();

      for (const id of batch) {
        await this.getById(id).removeLabel(labelId);
      }

      await this.commit();
    }

    return;
  }

  /**
   * Set `stage` by IDs
   * @param ids The IDs of the leads for which the `stage` will be set.
   * @param stageId The new stage ID.
   */
  async setStageByIds(ids: ILead['id'][], stageId: ILead['stage']) {
    if (ids.length === 0 || !stageId) {
      return;
    }

    const batches = splitEvery(250, ids);

    for (const batch of batches) {
      this.begin();

      for (const id of batch) {
        await this.getById(id).set({
          stage: stageId,
          stages: {
            [stageId]: serverTimestamp(),
          },
          status: deleteField(), // Business requirement: `status` should be deleted when new stage is set
        });
      }

      await this.commit();
    }

    return;
  }

  /**
   * Set `status` by IDs
   * @param ids The IDs of the leads for which the `status` will be set.
   * @param statusId The new status ID.
   */
  async setStatusByIds(ids: ILead['id'][], statusId: ILead['status'] | null) {
    if (ids.length === 0) return;

    const batches = splitEvery(250, ids);

    for (const batch of batches) {
      this.begin();

      for (const id of batch) {
        await this.getById(id).setStatus(statusId);
      }

      await this.commit();
    }

    return;
  }

  /**
   * Set `read` by IDs
   * @param ids The IDs of the leads that will be marked as `read`.
   */
  async setReadByIds(ids: ILead['id'][]) {
    if (ids.length === 0) {
      return;
    }

    const batches = splitEvery(250, ids);

    for (const batch of batches) {
      this.begin();

      for (const id of batch) {
        await this.getById(id).set({
          actions: {
            read: serverTimestamp(),
          },
        });
      }

      await this.commit();
    }

    return;
  }

  /**
   * Set `not read` by IDs
   * @param ids The IDs of the leads that will be marked as `not read`.
   */
  async setNotReadByIds(ids: ILead['id'][]) {
    if (ids.length === 0) {
      return;
    }

    const batches = splitEvery(250, ids);

    for (const batch of batches) {
      this.begin();

      for (const id of batch) {
        await this.getById(id).set({
          actions: {
            read: false,
          },
        });
      }

      await this.commit();
    }

    return;
  }

  /**
   * Set `replied` by IDs
   * @param ids The IDs of the leads that will be marked as `replied`.
   */
  async setRepliedByIds(ids: ILead['id'][]) {
    if (ids.length === 0) {
      return;
    }

    const batches = splitEvery(250, ids);

    for (const batch of batches) {
      this.begin();

      for (const id of batch) {
        await this.getById(id).set({
          actions: {
            replied: serverTimestamp(),
          },
        });
      }

      await this.commit();
    }

    return;
  }

  /**
   * Set `not replied` by IDs
   * @param ids The IDs of the leads that will be marked as `not replied`.
   */
  async setNotRepliedByIds(ids: ILead['id'][]) {
    if (ids.length === 0) {
      return;
    }

    const batches = splitEvery(250, ids);

    for (const batch of batches) {
      this.begin();

      for (const id of batch) {
        await this.getById(id).set({
          actions: {
            replied: false,
          },
        });
      }

      await this.commit();
    }

    return;
  }

  /**
   * Set `starred` by IDs
   * @param ids The IDs of the leads that will be marked as `starred`.
   */
  async setStarredByIds(ids: ILead['id'][]) {
    if (ids.length === 0) {
      return;
    }

    const batches = splitEvery(250, ids);

    for (const batch of batches) {
      this.begin();

      for (const id of batch) {
        await this.getById(id).set({
          actions: {
            starred: serverTimestamp(),
          },
        });
      }

      await this.commit();
    }

    return;
  }

  /**
   * Set `not starred` by IDs
   * @param ids The IDs of the leads that will be marked as `not starred`.
   */
  async setNotStarredByIds(ids: ILead['id'][]) {
    if (ids.length === 0) {
      return;
    }

    const batches = splitEvery(250, ids);

    for (const batch of batches) {
      this.begin();

      for (const id of batch) {
        await this.getById(id).set({
          actions: {
            starred: false,
          },
        });
      }

      await this.commit();
    }

    return;
  }
}
